import { useState } from 'react';
import { apiFetch } from '../../api';
import { Sparkles, ArrowRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { getLayoutedElements } from '@/utils/flowLayout';
import { useReactFlow } from 'reactflow';

export const AICommandBar = ({ onApply }: { onApply: (nodes: any[], edges: any[]) => void }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const { setNodes, setEdges } = useReactFlow();

    const handleGenerate = async () => {
        if (!prompt.trim()) return;
        setLoading(true);
        try {
            // apiFetch handles base URL and token (via localStorage 'token' or 'authToken') automatically.
            const data = await apiFetch('/ai/text-to-flow', null, {
                method: 'POST',
                body: JSON.stringify({ prompt })
            });

            if (data.nodes && data.nodes.length > 0) {
                // Auto Layout the new nodes
                const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
                    data.nodes.map((n: any) => ({ ...n, position: { x: 0, y: 0 }, data: { ...n.data, label: n.label || n.properties?.label || n.type } })), // Normalize
                    data.edges
                );

                // For now, we replace. In future, we could merge.
                setNodes(layoutedNodes);
                setEdges(layoutedEdges);

                setIsOpen(false);
                setPrompt("");
                toast.success(`Generated ${layoutedNodes.length} nodes from your prompt!`);
            } else {
                toast.warning("AI didn't generate any valid nodes.");
            }
        } catch (e: any) {
            console.error(e);
            toast.error("Failed to generate flow: " + (e.message || e));
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-white/90 backdrop-blur border border-purple-200 shadow-xl rounded-full px-4 py-2 flex items-center gap-2 text-sm font-medium text-purple-700 hover:scale-105 transition-all animate-in fade-in slide-in-from-top-4"
            >
                <Sparkles className="h-4 w-4" />
                Ask AI to build flow...
            </button>
        );
    }

    return (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 w-[500px] max-w-[90vw] animate-in fade-in slide-in-from-top-4">
            <div className="bg-white rounded-xl shadow-2xl border border-purple-100 overflow-hidden">
                <div className="p-1 flex items-center gap-2 bg-gradient-to-r from-purple-50 to-white">
                    <Sparkles className="h-5 w-5 text-purple-600 ml-2" />
                    <input
                        autoFocus
                        className="flex-1 bg-transparent border-none outline-none text-sm p-3 placeholder:text-gray-400"
                        placeholder="e.g. Load Sales.csv, filter Region=West, and pivot by Product..."
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleGenerate()}
                    />
                    {loading ? (
                        <Loader2 className="h-4 w-4 text-purple-600 animate-spin mr-2" />
                    ) : (
                        <button
                            onClick={handleGenerate}
                            className="p-2 hover:bg-purple-100 rounded-lg text-purple-700 transition"
                        >
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    )}
                    <button
                        onClick={() => setIsOpen(false)}
                        className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
};
